import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 5;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement | HTMLVideoElement | HTMLIFrameElement} image - The media element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    
    // Mouse events tracking
    this.addEventListener('mousedown', this.#handleMouseDown, options);
    window.addEventListener('mousemove', this.#handleMouseMove, options);
    window.addEventListener('mouseup', this.#handleMouseUp, options);
    this.addEventListener('click', this.#handleClick, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0) return; // Only track left click
    preventDefault(event);

    const currentTime = performance.now();
    const touch = { clientX: event.clientX, clientY: event.clientY };

    this.#startDragGestureFromTouch(touch);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;
    preventDefault(event);
    const touch = { clientX: event.clientX, clientY: event.clientY };
    this.#processDragGesture(touch);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseUp = (event) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#requestUpdateTransform();
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    // If the user was dragging the image to pan around, don't trigger a click zoom
    if (this.#hasDraggedBeyondThreshold) return;
    preventDefault(event);
    const touch = { clientX: event.clientX, clientY: event.clientY };
    this.#handleDoubleTapFromTouch(touch);
  }

  // PUBLIC API FOR UI CONTROLS
  
  zoomIn(amount = 0.5) {
    const containerRect = this.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    this.#zoomToPoint(clamp(this.#scale + amount, MIN_ZOOM, MAX_ZOOM), centerX, centerY);
  }
  
  zoomOut(amount = 0.5) {
    const containerRect = this.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    const newZoom = clamp(this.#scale - amount, MIN_ZOOM, MAX_ZOOM);
    this.#zoomToPoint(newZoom, centerX, centerY);
  }
  
  resetZoomToStart() {
    this.#zoomToPoint(MIN_ZOOM, this.getBoundingClientRect().left + this.clientWidth / 2, this.getBoundingClientRect().top + this.clientHeight / 2);
  }

  // Rest of original touch logic
  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);
        if (distance < DOUBLE_TAP_DISTANCE) {
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; 
          this.#lastTapPosition = null;
          return;
        }
      }

      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  #zoomToPoint(targetZoom, clientX, clientY) {
    const containerRect = this.getBoundingClientRect();
    const containerCenter = {
      x: containerRect.left + containerRect.width / 2,
      y: containerRect.top + containerRect.height / 2,
    };

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = targetZoom;

      const distanceFromCenter = {
        x: clientX - containerCenter.x,
        y: clientY - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; 
    }

    this.#hasManualZoom = true;
    this.#requestUpdateTransform();
  }

  #handleDoubleTapFromTouch(touch) {
    let targetZoom;
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
    } else {
      const tolerance = 0.05;
      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        targetZoom = 1.5; // Toggle to 1.5x zoom
      } else {
        targetZoom = MIN_ZOOM;
      }
    }

    this.#zoomToPoint(targetZoom, touch.clientX, touch.clientY);
  }

  #handleTouchMove = (event) => {
    preventDefault(event);
    const touchCount = event.touches.length;
    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  #processZoomGesture(touch1, touch2) {
    const oldScale = this.#scale;
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;

    // Use regular zoom calculations to maintain pan coordinates correctly based on touch origin
    const targetZoom = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.#scale = targetZoom;
    this.#hasManualZoom = true;

    const containerRect = this.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const containerCenterY = containerRect.top + containerRect.height / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);
    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;
    this.#requestUpdateTransform();
  }

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else if (imageElement.videoWidth > 0 && imageElement.videoHeight > 0) {
      naturalWidth = imageElement.videoWidth;
      naturalHeight = imageElement.videoHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.height;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;
    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  #resetZoom = () => {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;
  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}
